import { CONCEPT_ICONS, STATE_ICONS } from "@/components/ui/concept-icons";
/**
 * Execution sidebar — shows agent execution progress with its todos: a
 * compact checklist from todowrite tool output. Shows first 4 items by
 * default; expandable for more.
 */

import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import type { ExecutionTodo } from "@/stores/companion-store";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Overall execution state — drives the header toggle button. */
export type ExecutionState =
  | "idle"
  | "running"
  | "approval"
  | "completed"
  | "failed";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ExecutionSidebarProps {
  executionState: ExecutionState;
  /** True only while the agent is actively responding to a turn. */
  isAgentResponding?: boolean;
  todos: ExecutionTodo[];
  onRun?: () => void;
  onStop?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

// ---------------------------------------------------------------------------
// Status icon — per-item status indicator
// ---------------------------------------------------------------------------

type ItemStatus = "pending" | "running" | "completed" | "failed";

function StatusIcon({ status }: { status: ItemStatus }) {
  switch (status) {
    case "completed":
      return (
        <STATE_ICONS.done
          className="h-appkit-sm w-appkit-sm text-emerald-400"
          aria-hidden="true"
        />
      );
    case "running":
      return (
        <Icons.Spinner
          className="h-appkit-sm w-appkit-sm animate-spin text-blue-400"
          aria-hidden="true"
        />
      );
    case "failed":
      return (
        <STATE_ICONS.error
          className="h-appkit-sm w-appkit-sm text-red-400"
          aria-hidden="true"
        />
      );
    case "pending":
    default:
      return (
        <Icons.Circle
          className="h-appkit-sm w-appkit-sm text-muted"
          aria-hidden="true"
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Header action — toggle button driven by execution state
// ---------------------------------------------------------------------------

function HeaderAction({
  executionState,
  onStop,
  onRun,
  onApprove,
  onReject,
}: {
  executionState: ExecutionState;
  onStop?: () => void;
  onRun?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}) {
  if (executionState === "approval" && onApprove && onReject) {
    return (
      <span className="flex items-center gap-appkit-2xs">
        <button
          type="button"
          className="flex h-appkit-lg w-appkit-lg items-center justify-center rounded-control-sm text-emerald-400 transition-colors hover:bg-emerald-400/20"
          onClick={onApprove}
          data-testid="exec-approve-btn"
          aria-label="Allow"
        >
          <Icons.Check className="h-appkit-sm w-appkit-sm" />
        </button>
        <button
          type="button"
          className="flex h-appkit-lg w-appkit-lg items-center justify-center rounded-control-sm text-red-400 transition-colors hover:bg-red-400/20"
          onClick={onReject}
          data-testid="exec-reject-btn"
          aria-label="Deny"
        >
          <Icons.X className="h-appkit-sm w-appkit-sm" />
        </button>
      </span>
    );
  }

  if (executionState === "running") {
    return (
      <button
        type="button"
        className="flex h-appkit-lg w-appkit-lg items-center justify-center rounded-control-sm text-secondary transition-colors hover:bg-glass-fill-heavy hover:text-primary"
        onClick={onStop}
        data-testid="exec-stop-btn"
        aria-label="Stop"
      >
        <Icons.Square className="h-appkit-xs w-appkit-xs" />
      </button>
    );
  }

  // idle, completed, failed → play button
  return (
    <button
      type="button"
      className="flex h-appkit-lg w-appkit-lg items-center justify-center rounded-control-sm text-secondary transition-colors hover:bg-glass-fill-heavy hover:text-primary"
      onClick={onRun}
      data-testid="exec-run-btn"
      aria-label="Run"
    >
      <Icons.Play className="h-appkit-xs w-appkit-xs" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Todos section
// ---------------------------------------------------------------------------

const INITIAL_VISIBLE = 4;

function TodosSection({ todos }: { todos: ExecutionTodo[] }) {
  const [expanded, setExpanded] = useState(false);
  const visibleTodos = expanded ? todos : todos.slice(0, INITIAL_VISIBLE);
  const hiddenCount = todos.length - INITIAL_VISIBLE;

  return (
    <div data-testid="exec-todos-section">
      {/* Section header */}
      <div className="flex items-center gap-appkit-2xs px-appkit-sm py-appkit-2xs">
        <CONCEPT_ICONS.task className="h-appkit-sm w-appkit-sm flex-shrink-0 text-muted" />
        <span className="text-appkit-mini font-medium uppercase tracking-wider text-muted">
          Tasks
        </span>
      </div>

      {/* Todo list */}
      <div className="flex flex-col px-appkit-2xs">
        {visibleTodos.map((todo) => (
          <div
            key={todo.id}
            className={cn(
              "rounded-control-sm px-appkit-xs py-appkit-2xs",
              todo.status === "failed" && "bg-red-400/10",
            )}
            data-testid={`exec-todo-${todo.id}`}
          >
            <div className="flex items-center gap-appkit-xs">
              <span className="flex h-appkit-md w-appkit-md flex-shrink-0 items-center justify-center">
                <StatusIcon status={todo.status} />
              </span>
              <span
                className={cn(
                  "flex-1 truncate text-appkit-mini",
                  todo.status === "running"
                    ? "font-medium text-primary"
                    : todo.status === "completed"
                      ? "text-secondary"
                      : todo.status === "failed"
                        ? "text-red-400"
                        : "text-muted",
                )}
              >
                {todo.title}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Expand / collapse toggle */}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="w-full px-appkit-sm py-appkit-2xs text-left text-appkit-mini text-accent transition-colors"
          onClick={() => setExpanded((prev) => !prev)}
          data-testid="exec-todos-expand-btn"
        >
          {expanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExecutionSidebar — main component
// ---------------------------------------------------------------------------

export function ExecutionSidebar({
  executionState,
  isAgentResponding = false,
  todos,
  onRun,
  onStop,
  onApprove,
  onReject,
}: ExecutionSidebarProps) {
  const headerTitle =
    executionState === "running" && isAgentResponding
      ? "Working..."
      : executionState === "running"
        ? "Connected"
        : executionState === "completed"
          ? "Completed"
          : executionState === "failed"
            ? "Failed"
            : executionState === "approval"
              ? "Awaiting approval"
              : "Idle";

  return (
    <div className="flex h-full flex-col" data-testid="execution-sidebar">
      {/* Header */}
      <div
        className="flex items-center gap-appkit-2xs px-appkit-sm py-appkit-xs"
        data-testid={`exec-sidebar-${executionState}`}
      >
        <CONCEPT_ICONS.agent className="h-appkit-sm w-appkit-sm flex-shrink-0 text-accent" />
        <span className="flex-1 truncate text-appkit-small font-semibold text-primary">
          {headerTitle}
        </span>
        <HeaderAction
          executionState={executionState}
          onStop={onStop}
          onRun={onRun}
          onApprove={onApprove}
          onReject={onReject}
        />
      </div>

      {/* Todos section */}
      {todos.length > 0 && <TodosSection todos={todos} />}

      {/* Empty state — no todos */}
      {todos.length === 0 && (
        <p className="px-appkit-sm py-appkit-sm text-center text-appkit-mini text-muted">
          No activity yet
        </p>
      )}
    </div>
  );
}
